// 2 adds `bye`. Purely ADDITIVE — decoding is version-blind (see decodeChat) and
// nothing anywhere compares `v`, so a v1 and a v2 peer interoperate: the v1 side
// simply doesn't recognise `bye` and falls through to the receiver's grace
// window, which is the old behaviour. Bumped to keep the constant honest about
// the wire format, not to gate anything. Do NOT start gating on it without
// planning the rollout — both apps deploy independently.
export const CHAT_PROTOCOL_VERSION = 2;
export const TYPING_THROTTLE_MS = 2000;
export const TYPING_TIMEOUT_MS = 5000;

export type ChatMsg = { v: number; type: "msg"; id: string; text: string; ts: number };
export type ChatTyping = { v: number; type: "typing"; state: "start" | "stop"; ts: number };
/**
 * "I am leaving on purpose" — sent immediately before a peer tears its room
 * down, so the other side can distinguish a deliberate hang-up from a
 * connection loss. LiveKit's ParticipantDisconnected carries no reason, so
 * without this the two are identical on the wire, and the only safe response to
 * a peer vanishing is to wait out a grace window in case they come back. A bye
 * says "don't wait".
 *
 * Best-effort by nature: a peer whose network already died cannot send one, and
 * that is exactly the case the grace window still covers. Carries no payload —
 * the fact of it IS the payload.
 */
export type ChatBye = { v: number; type: "bye"; ts: number };
export type ChatEnvelope = ChatMsg | ChatTyping | ChatBye;

const enc = new TextEncoder();
const dec = new TextDecoder();

export function newMessageId(): string {
  return crypto.randomUUID();
}

export function encodeChat(env: ChatEnvelope): Uint8Array {
  return enc.encode(JSON.stringify(env));
}

/** Tolerant decode: unknown/malformed payloads return null and are ignored. */
export function decodeChat(bytes: Uint8Array): ChatEnvelope | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(dec.decode(bytes));
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const o = parsed as Record<string, unknown>;
  if (typeof o.v !== "number" || typeof o.ts !== "number") return null;
  if (o.type === "msg" && typeof o.id === "string" && typeof o.text === "string") {
    return { v: o.v, type: "msg", id: o.id, text: o.text, ts: o.ts };
  }
  if (o.type === "typing" && (o.state === "start" || o.state === "stop")) {
    return { v: o.v, type: "typing", state: o.state, ts: o.ts };
  }
  if (o.type === "bye") {
    return { v: o.v, type: "bye", ts: o.ts };
  }
  return null;
}

export function shouldSendTyping(lastSentMs: number | null, nowMs: number): boolean {
  return lastSentMs === null || nowMs - lastSentMs >= TYPING_THROTTLE_MS;
}

export function typingExpired(lastReceivedMs: number, nowMs: number): boolean {
  return nowMs - lastReceivedMs >= TYPING_TIMEOUT_MS;
}
